import { z } from "zod";
import { type PillowShape, PillowShapeSchema } from "./enums.js";
import type { ProjectConfigSize, ProjectSizeCm } from "./project-config.js";
import { STATIC_RECTANGLE_SIZE_CM, type StaticRectangleSize } from "./static-assets.js";

// The pillow `product_variants` rows are keyed on a size code. Silhouette uses the
// two price tiers (S / L); rectangle offers all three (S / M / L). Kept as its own
// literal set (not `ProjectConfigSize`) because it is the *variant* axis, not the
// buyer's live size choice — even though the two currently share the S/M/L values.
export const PILLOW_VARIANT_SIZE_CODES = ["S", "M", "L"] as const;
export type PillowVariantSizeCode = (typeof PILLOW_VARIANT_SIZE_CODES)[number];

// The full attribute descriptor stored in `product_variants.attributes` for a pillow
// — the generic per-type jsonb the fixed `size_code` / `shape` / `size_cm` columns
// were folded into (ADR-0054 §1). Snake_case matches the jsonb storage convention
// (like `projects.config`), so api / worker / frontend read the same record. The
// priced axes are `shape` + `size_code`; `size_cm` is the nominal producer dimension
// carried alongside them (not a pricing axis — see `PillowVariantMatch`).
//
// `shipping_size_class` (#406) is the PER-VARIANT shipping-size class the cart oversize
// rule sums (ADR-0054 §4). Representation: a small POSITIVE INTEGER of "parcel size
// units" the variant occupies in a single shipment — a silhouette S / rectangle S is 1
// unit, the larger tiers are 2 (see `PILLOW_VARIANT_CATALOG`). `shippingForCart` sums
// `units × quantity` across the cart and compares the total against the operator's
// Paczkomat-capacity threshold (in the SAME units); it is deliberately NOT a priced axis
// (`PillowVariantMatch` excludes it) — it never affects the variant a config resolves to,
// only which delivery method can carry the whole order. It lives inside `attributes` so
// the admin Katalog editor (#404, generic jsonb editor) edits it with no rework; the
// pillow's `variant_schema` declares it as an `int` axis so an edit is validated.
export const PillowVariantAttributes = z.object({
  shape: PillowShapeSchema,
  size_code: z.enum(PILLOW_VARIANT_SIZE_CODES),
  size_cm: z.number().int().positive(),
  shipping_size_class: z.number().int().positive(),
});
export type PillowVariantAttributes = z.infer<typeof PillowVariantAttributes>;

// The **3D print** ("obraz 3D") variant axes stored in `product_variants.attributes`
// (ADR-0054 §1, #407) — the second Product type on the same generic spine. Its priced
// axes are **size + colour depth** (unlike the pillow's shape + size): `size_code` is
// the S/M/L tier, `color_depth` is how richly the pet photo is rendered as a colour
// relief (mono → duo → full). `size_cm` is the nominal longest-side producer dimension
// (single int, like the pillow's — schema debt shared with ADR-0048), and
// `shipping_size_class` is the same per-variant parcel-size units the cart oversize rule
// sums (#406). Snake_case matches the jsonb storage convention. The generation pipeline
// (heightmap / 3MF / print prep) is a SEPARATE track (ADR-0054 out of scope) — this is
// the commerce-layer catalog only.
export const THREE_D_PRINT_SIZE_CODES = ["S", "M", "L"] as const;
export type ThreeDPrintSizeCode = (typeof THREE_D_PRINT_SIZE_CODES)[number];
export const THREE_D_PRINT_COLOR_DEPTHS = ["mono", "duo", "full"] as const;
export type ThreeDPrintColorDepth = (typeof THREE_D_PRINT_COLOR_DEPTHS)[number];

export const ThreeDPrintVariantAttributes = z.object({
  size_code: z.enum(THREE_D_PRINT_SIZE_CODES),
  color_depth: z.enum(THREE_D_PRINT_COLOR_DEPTHS),
  size_cm: z.number().int().positive(),
  shipping_size_class: z.number().int().positive(),
});
export type ThreeDPrintVariantAttributes = z.infer<typeof ThreeDPrintVariantAttributes>;

// The `product_variants.attributes` jsonb interpreted per Product type (ADR-0054 §1):
// a pillow row carries `PillowVariantAttributes`, a 3D-print row `ThreeDPrintVariantAttributes`.
// There is no discriminant field INSIDE the jsonb — the row's sibling `product_type`
// column is the discriminant — so a reader narrows by the presence of a type-unique key
// (`"shape" in attrs` ⇒ pillow) or by the row's `product_type`. NOTE: the Drizzle column
// keeps its `$type<PillowVariantAttributes>()` for now (the pillow-only read paths —
// producer packet, admin, order-view, emails — are a later multi-type track, ADR-0054),
// so those paths read the pillow shape; the commerce paths (cart / checkout) title a
// line through `variantTitlePl`, which handles both.
export type VariantAttributes = PillowVariantAttributes | ThreeDPrintVariantAttributes;

// The shipping-size units a variant occupies, read off its `attributes` for the cart
// oversize sum (#406). Both types carry `shipping_size_class`, so this reads it
// structurally for ANY type. A stored row is guaranteed to carry the axis (seed +
// backfill migrations 0020/0021), but a stale/hand-written row missing it must never
// crash the pricing path — it falls back to ONE unit (the smallest, safest assumption).
export const DEFAULT_SHIPPING_SIZE_UNITS = 1;
export function variantShippingSizeUnits(attributes: { shipping_size_class?: number }): number {
  const units = attributes.shipping_size_class;
  return typeof units === "number" && units > 0 ? units : DEFAULT_SHIPPING_SIZE_UNITS;
}

// The SUBSET of a variant's attributes a Product's config prices against — the priced
// axes only (`attributes @> key`). `resolveVariant` returns one of these per type; the
// server looks the one matching `product_variants` row up by it (jsonb containment) for
// the snapshotted price. `size_cm` is deliberately absent: it is the resolved row's data,
// not an axis a config selects on. A pillow prices on shape + size; a 3D print on size +
// colour depth (ADR-0054 §1, #407).
export interface PillowVariantMatch {
  shape: PillowShape;
  size_code: PillowVariantSizeCode;
}
export interface ThreeDPrintVariantMatch {
  size_code: ThreeDPrintSizeCode;
  color_depth: ThreeDPrintColorDepth;
}
export type VariantMatch = PillowVariantMatch | ThreeDPrintVariantMatch;

// The MVP pillow catalog (ADR-0054 §1, single-material #245): 5 variants — 2
// silhouette tiers (S / L) + 3 rectangle sizes (S=40×40, M=50×50, L=40×60). The
// exhaustive set `resolveVariant` can match and the source of truth the seed builds
// its `product_variants` rows from (`base_price_cents` is attached DB-side, keyed by
// `shape` + `size_code`). `size_cm` is the nominal producer dimension (a single int;
// the non-square 40×60 carries a nominal 60 — schema debt, ADR-0048).
//
// `shipping_size_class` is the parcel size units each variant occupies (#406): the small
// tier (S, whichever shape) is 1 unit, the larger tiers (silhouette L, rectangle M/L) are
// 2 — so, at the default Paczkomat threshold of 6 units, roughly three large pillows still
// fit a locker before an order tips to Kurier. Operator-editable per-variant in the admin
// Katalog tab; these are the seed placeholders (migration 0020 backfills them).
export const PILLOW_VARIANT_CATALOG: readonly PillowVariantAttributes[] = [
  { shape: "silhouette", size_code: "S", size_cm: 40, shipping_size_class: 1 },
  { shape: "silhouette", size_code: "L", size_cm: 60, shipping_size_class: 2 },
  { shape: "rectangle", size_code: "S", size_cm: 40, shipping_size_class: 1 },
  { shape: "rectangle", size_code: "M", size_cm: 50, shipping_size_class: 2 },
  { shape: "rectangle", size_code: "L", size_cm: 60, shipping_size_class: 2 },
];

// The MVP 3D-print catalog (ADR-0054 §1, #407): the exhaustive size × colour-depth
// cross-product (3 sizes × 3 depths = 9 variants), so EVERY valid selection resolves to
// exactly one row — the source of truth the seed builds its `product_variants` rows from
// (`base_price_cents` is attached DB-side, keyed by `size_code` + `color_depth`).
// `size_cm` is the nominal longest-side print dimension (S=15 / M=20 / L=30 cm); the
// small tier is 1 parcel size unit and the large tier 2, so the cart oversize rule sums
// a mixed pillow + 3D-print cart on ONE scale (#406). Operator-editable per-variant in
// the admin Katalog tab; these are the seed placeholders (migration 0021 seeds the row).
export const THREE_D_PRINT_VARIANT_CATALOG: readonly ThreeDPrintVariantAttributes[] = [
  { size_code: "S", color_depth: "mono", size_cm: 15, shipping_size_class: 1 },
  { size_code: "S", color_depth: "duo", size_cm: 15, shipping_size_class: 1 },
  { size_code: "S", color_depth: "full", size_cm: 15, shipping_size_class: 1 },
  { size_code: "M", color_depth: "mono", size_cm: 20, shipping_size_class: 1 },
  { size_code: "M", color_depth: "duo", size_cm: 20, shipping_size_class: 1 },
  { size_code: "M", color_depth: "full", size_cm: 20, shipping_size_class: 1 },
  { size_code: "L", color_depth: "mono", size_cm: 30, shipping_size_class: 2 },
  { size_code: "L", color_depth: "duo", size_cm: 30, shipping_size_class: 2 },
  { size_code: "L", color_depth: "full", size_cm: 30, shipping_size_class: 2 },
];

// The tagged, per-type input `resolveVariant` maps to a priced-variant key — the priced
// axes only, discriminated by `product_type` (ADR-0054, #407). Callers construct one
// from a Product's config or a cart Variant pick; the pillow branch reads shape + size,
// the 3D-print branch size + colour depth. Kept minimal (not the whole config) so both a
// live config and a no-preview cart selection resolve identically.
export type VariantResolvable =
  | { product_type: "pillow"; shape: PillowShape; size: ProjectConfigSize }
  | { product_type: "3d_print"; size: ProjectConfigSize; color_depth: ThreeDPrintColorDepth };

// Maps a Product's chosen axes to the ONE variant it prices against — the generalised,
// per-type `resolveVariant` (ADR-0054 §1, #407). Dispatches on `product_type`:
//   - **pillow** — shape + size; silhouette is a 2-tier slider (S / L), so the 3-value
//     size rounds M up to the larger tier (M→L); rectangle offers all three 1:1.
//   - **3D print** — size + colour depth map 1:1 to the priced `{ size_code, color_depth }`.
// Pure + shared: the configurator, the Product create, and the checkout re-price all
// resolve the variant identically, server-authoritatively — the client never sends a
// price. Returns the priced-axis key the repo matches by jsonb containment (`attributes @>`).
export function resolveVariant(config: VariantResolvable): VariantMatch {
  switch (config.product_type) {
    case "pillow": {
      const size_code: PillowVariantSizeCode =
        config.shape === "silhouette" ? (config.size === "S" ? "S" : "L") : config.size;
      return { shape: config.shape, size_code };
    }
    case "3d_print":
      return { size_code: config.size, color_depth: config.color_depth };
  }
}

// The rectangle catalogue's S/M/L pricing tiers map 1:1 to their fixed footprints
// (seed order: S=40×40, M=50×50, L=40×60). `satisfies` pins the map to exactly the
// pillow size codes so it can't drift from the catalogue.
const RECTANGLE_SIZE_CODE_TO_STATIC = {
  S: "40x40",
  M: "50x50",
  L: "40x60",
} satisfies Record<PillowVariantSizeCode, StaticRectangleSize>;

// The footprint (whole-cm W × H) a pillow Product takes when its Variant is picked in
// the CART with no preview slider (ADR-0054 §2, #405). Server-authoritative, derived
// from the resolved variant + the sibling's committed footprint:
//   - **rectangle** — the catalogue's EXACT cm for the size (40×40 / 50×50 / 40×60);
//     the sibling's aspect is irrelevant (each rectangle size has its own shape).
//   - **silhouette** — the pet's committed ASPECT preserved, rescaled so the LONGEST
//     side is the variant's nominal `size_cm` (S≈40 / L≈60). The preview's photo-tuned
//     EXACT footprint isn't recomputable without the slider (it needs the measured mesh
//     extent + the operator's shipping boxes), so this nominal footprint is the honest
//     no-preview stand-in. Falls back to a square when there is no sibling footprint.
export function cartVariantDimensionsCm(
  attributes: PillowVariantAttributes,
  siblingDimsCm: ProjectSizeCm | null | undefined,
): ProjectSizeCm {
  if (attributes.shape === "rectangle") {
    const [width, height] =
      STATIC_RECTANGLE_SIZE_CM[RECTANGLE_SIZE_CODE_TO_STATIC[attributes.size_code]];
    return { width, height };
  }
  const longCm = attributes.size_cm;
  const siblingLong = siblingDimsCm ? Math.max(siblingDimsCm.width, siblingDimsCm.height) : 0;
  if (!siblingDimsCm || siblingLong <= 0) {
    return { width: longCm, height: longCm };
  }
  const k = longCm / siblingLong;
  return {
    width: Math.max(1, Math.round(siblingDimsCm.width * k)),
    height: Math.max(1, Math.round(siblingDimsCm.height * k)),
  };
}

// Buyer-facing PL product name for a shape — the single source of truth shared by
// the transactional emails (order confirmation #227, shipped/delivered #315). The
// MVP catalog is single-material (#245), so the shape alone names the product; the
// size (S/M/L + cm) is shown as its own line by whatever renders this. Kept here
// (with the variant vocabulary) so the email copy can never drift per template.
const PILLOW_SHAPE_LABEL_PL: Record<PillowShape, string> = {
  silhouette: "Poduszka z sylwetką pupila",
  rectangle: "Poduszka prostokątna z pupilem",
};
export function productLabelPl(shape: PillowShape): string {
  return PILLOW_SHAPE_LABEL_PL[shape];
}

// Buyer-facing PL name of a 3D print's colour depth (ADR-0054, #407) — the priced axis
// that isn't a size, surfaced in the line title so two 3D prints of the same size but
// different depth read as distinct products.
const THREE_D_PRINT_COLOR_DEPTH_LABEL_PL: Record<ThreeDPrintColorDepth, string> = {
  mono: "mono",
  duo: "dwukolorowy",
  full: "pełny kolor",
};

// Buyer-facing PL title of ONE order/cart line of ANY Product type — the single source of
// truth shared by the cart quote, the mini-cart, the checkout list, and the Stripe receipt
// line (ADR-0054 §3, #407), so the same product is named identically everywhere. Narrows the
// per-type `attributes` union at RUNTIME by the type-unique `shape` key (a stored pillow row
// carries it; a 3D-print row does not — the Drizzle column is typed pillow, but the real
// jsonb routes correctly here). A pillow reads its shape label; a 3D print its colour depth.
export function variantTitlePl(attributes: VariantAttributes): string {
  if ("shape" in attributes) return productLabelPl(attributes.shape);
  // Guard the depth lookup: an unexpected / missing `color_depth` (a stale or
  // hand-written row) must never render "(undefined)" in an email or the Order
  // view — drop the parenthetical entirely rather than surface a broken label.
  const depthLabel = THREE_D_PRINT_COLOR_DEPTH_LABEL_PL[attributes.color_depth];
  return depthLabel ? `Obraz 3D pupila (${depthLabel})` : "Obraz 3D pupila";
}
